import { getExhibitionCountryApiName, normalizeLocationKey } from './exhibitionLocations';

const API_URL = 'https://countriesnow.space/api/v0.1/countries/cities';
const CACHE_PREFIX = 'exhibition-cities-v1:';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const memoryCache = new Map();

function readSessionCache(apiCountry) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_PREFIX}${apiCountry}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.cities?.length || Date.now() - parsed.fetchedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(`${CACHE_PREFIX}${apiCountry}`);
      return null;
    }
    return parsed.cities;
  } catch {
    return null;
  }
}

function writeSessionCache(apiCountry, cities) {
  try {
    sessionStorage.setItem(
      `${CACHE_PREFIX}${apiCountry}`,
      JSON.stringify({ fetchedAt: Date.now(), cities }),
    );
  } catch {
    /* quota or private mode — memory cache still helps */
  }
}

async function requestCities(apiCountry) {
  const res = await fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country: apiCountry }),
  });
  if (!res.ok) throw new Error(`City lookup failed (${res.status})`);
  const json = await res.json();
  if (json?.error || !Array.isArray(json?.data)) {
    throw new Error(json?.msg || 'City lookup failed');
  }
  const cities = json.data
    .map((name) => String(name || '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  return cities;
}

/** Fetch all cities for a stored exhibition country (cached in memory + session). */
export async function fetchExhibitionCountryCities(country) {
  const apiCountry = getExhibitionCountryApiName(country);
  if (!apiCountry) return [];

  const cacheKey = normalizeLocationKey(apiCountry);
  if (memoryCache.has(cacheKey)) return memoryCache.get(cacheKey);

  const sessionCached = readSessionCache(apiCountry);
  if (sessionCached?.length) {
    memoryCache.set(cacheKey, sessionCached);
    return sessionCached;
  }

  const cities = await requestCities(apiCountry);
  memoryCache.set(cacheKey, cities);
  writeSessionCache(apiCountry, cities);
  return cities;
}
